import { useTranslation } from "react-i18next";
import type { ConversationListItemDto } from "@multi-chat/shared";
import { LtrValue } from "../../../components/common/LtrValue.js";
import { cn } from "../../../lib/utils.js";
import { ContactStateMark } from "../ContactStateMark.js";
import { ConversationAvatar } from "../ConversationAvatar/ConversationAvatar.js";
import type { ConversationListRowPreview } from "../conversation-draft-presentation.js";
import { conversationRowUnreadLabel } from "../conversation-list-row.js";
import { isPhoneLikeDisplay } from "../inbox-display.js";
import { MemberInitials } from "../ConversationAssigneeControl.js";
import { isWorkflowClosed } from "../ConversationStatusControl.js";

export function ConversationListRow({
  item,
  title,
  preview,
  time,
  fullTime,
  activityAt,
  selected,
  onSelect,
}: {
  item: ConversationListItemDto;
  title: string;
  preview: ConversationListRowPreview;
  time: string;
  fullTime: string;
  activityAt: string | null;
  selected: boolean;
  onSelect: (conversationId: number) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const unreadLabel = conversationRowUnreadLabel(item.unreadCount);
  const unread = unreadLabel != null;
  return (
    <button
      type="button"
      data-conversation-id={item.conversationId}
      className={cn(
        "relative isolate mx-2 my-0.5 flex h-16 w-[calc(100%-1rem)] cursor-pointer items-center gap-2.5 rounded-xl px-2.5 py-[9px] text-start text-inherit transition-transform duration-200 ease-inbox motion-reduce:transition-none",
        "before:absolute before:inset-0 before:-z-10 before:rounded-[inherit] before:border before:border-inbox-line before:bg-surface before:opacity-0 before:shadow-inbox-float before:transition-opacity before:duration-200",
        "hover:-translate-y-px hover:before:opacity-100 motion-reduce:hover:translate-y-0",
        "focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-primary",
        selected &&
          "-translate-y-px before:border-[rgb(23_126_103/0.24)] before:opacity-100 before:shadow-inbox-selected after:absolute after:start-0 after:top-[18px] after:bottom-[18px] after:w-[3px] after:rounded-e-[3px] after:bg-primary motion-reduce:translate-y-0",
      )}
      onClick={() => onSelect(item.conversationId)}
    >
      <ConversationAvatar
        title={title}
        imageUrl={item.contactPlatformProfile.platformProfileImageUrl}
        platformType={item.platformType}
        toneSeed={item.contactPlatformProfile.id}
      />

      <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <span className="flex min-w-0 items-center gap-1.5">
          {/* `dir="auto"` keeps the ellipsis at the name's own end; alignment follows the UI side. */}
          <span
            className={cn(
              "min-w-0 flex-1 truncate text-[0.84375rem] leading-tight text-inbox-strong",
              i18n.dir() === "rtl" ? "text-right" : "text-left",
              unread ? "font-[640]" : "font-[530]",
            )}
            dir="auto"
          >
            {isPhoneLikeDisplay(title) ? <LtrValue className="truncate">{title}</LtrValue> : title}
          </span>
          <ContactStateMark state={item.contactPlatformProfile.contactState} />
          {item.assignedMember ? (
            <span className="inline-flex shrink-0 -translate-y-px self-center" title={item.assignedMember.displayName}>
              <MemberInitials
                name={item.assignedMember.displayName}
                memberId={item.assignedMember.memberId}
                inactive={!item.assignedMember.isActive}
                size="xs"
              />
            </span>
          ) : null}
          {isWorkflowClosed(item.status) ? (
            <span className="shrink-0 rounded-full bg-inbox-line-soft px-1.5 py-px text-[10px] font-semibold text-inbox-muted">
              {t("inbox.workflow.status.closedMarker")}
            </span>
          ) : null}
          {time ? (
            <time
              className={cn(
                "shrink-0 text-[0.6875rem] tabular-nums",
                unread ? "font-[560] text-primary" : "text-inbox-subtle",
              )}
              dateTime={activityAt ?? undefined}
              title={fullTime || undefined}
            >
              {time}
            </time>
          ) : null}
        </span>

        <span className="flex min-w-0 items-center gap-2">
          <span
            className={cn(
              "min-w-0 flex-1 truncate text-[0.78125rem] leading-snug",
              unread && preview.kind !== "draft" ? "text-[#33463f]" : "text-inbox-muted",
            )}
          >
            {preview.kind === "draft" ? (
              <>
                <span className="font-semibold text-inbox-draft">{t("inbox.draftPrefix")}</span>{" "}
                <span dir="auto">{preview.text}</span>
              </>
            ) : (
              <span dir="auto">{preview.text}</span>
            )}
          </span>
          {unread ? (
            <span
              className="inline-grid h-[18px] min-w-[18px] shrink-0 place-items-center rounded-full bg-primary px-[5px] text-[0.65625rem] leading-none font-semibold text-white tabular-nums"
              aria-label={t("inbox.unreadCount", { count: item.unreadCount })}
            >
              {unreadLabel}
            </span>
          ) : null}
        </span>
      </span>
    </button>
  );
}